import { createContext, useContext, useEffect } from 'react';

export interface AppBarBack {
  to: string;
  label: string;
}

export const AppBarContext = createContext<(back: AppBarBack | null) => void>(() => {});

/** Hides the mobile tab bar; used by focused, step-by-step flows. */
export const TabBarContext = createContext<(hidden: boolean) => void>(() => {});

/** Hides the mobile bottom tab bar while the calling page is mounted (Figma flows like R1–R6). */
export function useHideTabBar() {
  const setHidden = useContext(TabBarContext);
  useEffect(() => {
    setHidden(true);
    return () => setHidden(false);
  }, [setHidden]);
}

/** Opens the shell's logout confirmation (the same dialog as the sidebar's log-out button). */
export const RequestLogoutContext = createContext<() => void>(() => {});
export const useRequestLogout = () => useContext(RequestLogoutContext);

/** Shows a back link instead of the logo in the mobile app bar while the calling page is mounted. */
export function useAppBarBack(back?: AppBarBack) {
  const setBack = useContext(AppBarContext);
  const to = back?.to;
  const label = back?.label;
  useEffect(() => {
    if (!to || !label) return;
    setBack({ to, label });
    return () => setBack(null);
  }, [setBack, to, label]);
}
